import * as yup from 'yup';
import { SystemValidationError } from '../constants/errorCodes.js';

const strictObjectSchema = (schema) =>
  yup.object(schema).strict(true).noUnknown(true, SystemValidationError.INVALID_ADDITIONAL_FIELDS);

const mongoIdRegex = /^[a-fA-F0-9]{24}$/;

const validateDataWithSchema = async (schema, data, additionalContext) =>
  await schema.validate(data, { abortEarly: false, context: () => ({ data, ...additionalContext }) });

const makeOptionalAndRequiredSchemas = (baseSchema, requiredMessage = 'required') => [
  baseSchema.optional(),
  baseSchema.required(requiredMessage),
];

const isEmpty = (value) => value === undefined || value === null;

const isEmptyObject = (value) =>
  isEmpty(value) || Object.keys(value).length == 0 || Object.values(value).every((v) => v == undefined);

const isInteger = (value) => typeof value === 'number' && value === parseInt(value.toString());

const isSingleDigitDecimal = (value) => typeof value === 'number' && value === parseFloat(value.toFixed(1));

const makeOptionalAndRequiredObjectSchemas = (schema) => {
  const requiredSchema = strictObjectSchema(schema).required('required');

  const schemaOptional = Object.fromEntries(Object.entries(schema).map(([f, s]) => [f, s.optional()]));
  const optionalSchema = yup
    .object(schemaOptional)
    .test(
      (value, context) => isEmptyObject(value) || requiredSchema.validate(value, { abortEarly: false, ...context }),
    );
  return [optionalSchema, requiredSchema];
};

const makeOptionalObjectSchema = (schema) => makeOptionalAndRequiredObjectSchemas(schema)[0];

const ignoredTestName = null;

yup.addMethod(yup.object, 'testContext', function (message, test) {
  return this.test(ignoredTestName, message, function () {
    return test(this.options.context());
  });
});

const stringUntrimmed = () =>
  yup
    .string()
    .test('no-excessive-length', SystemValidationError.UNEXPECTED_SYSTEM_HANDLED, (value) =>
      value ? value.length < 1000 : true,
    );

const string = () =>
  stringUntrimmed().test('trimmed-required', SystemValidationError.UNEXPECTED_SYSTEM_HANDLED, (value) =>
    value ? value.trim() === value : true,
  );

const updatedYup = { ...yup, string, stringUntrimmed };

export {
  updatedYup as yup,
  strictObjectSchema,
  mongoIdRegex,
  validateDataWithSchema,
  isEmpty,
  isInteger,
  isSingleDigitDecimal,
  makeOptionalAndRequiredSchemas,
  makeOptionalAndRequiredObjectSchemas,
  makeOptionalObjectSchema,
  ignoredTestName,
};
